/**
 * A caixa "Calculadora Petrella", com as abas "Ajuda de Custo" e "Diaristas".
 *
 * Papel: reproduz `#modal-ajuda-custo`, `abrirCalculadoraAjudaCusto`,
 * `mudarAbaCalculadoraPetrella`, `repetirUltimoLancamentoAjudaCusto`,
 * `confirmarCalculadoraAjudaCusto` e `confirmarCalculadoraDiarista` (l.
 * 955–1096, 4775–4974, 5109–5154, 4880–4916): a caixa de 640 px, as duas abas,
 * e o botão do rodapé que muda de texto e de ação com a aba.
 *
 * Como usar: quem monta só monta a caixa quando ela está aberta, e por isso ela
 * abre sempre na primeira aba e com os campos zerados; a caixa fecha sozinha
 * depois de gravar, chamando `onFechar`. Os dois formulários ficam guardados
 * aqui, e trocar de aba não perde o que foi digitado na outra.
 *
 * "Repetir último lançamento" lê o grupo no clique, e não de uma leitura
 * anterior: o grupo é o que está gravado agora. O pagador vazio vale o pagador
 * padrão da casa na aba "Ajuda de Custo"; o da aba "Diaristas" não vai para a
 * API.
 */
import { MESES_POR_EXTENSO, itensDaCalculadora } from '@concierge/shared';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { lerValorDigitado } from '@/shared/lib/mascara';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  type CamposDaAjudaDeCusto,
  type CamposDaDiarista,
  camposIniciaisDaAjudaDeCusto,
  camposIniciaisDaDiarista,
  entradaDaCalculadora,
  favorecidoPeloNome,
  repetirGrupo,
  valorDosCamposDaDiarista,
} from '../calculadora';
import { useFavorecidos } from '../hooks/usar-financas';
import {
  useBuscarUltimoGrupo,
  useConfiguracao,
  useCriarPelaCalculadora,
  useNomesDaFolha,
} from '../hooks/usar-financas-extras';
import { AbaAjudaDeCusto } from './aba-ajuda-de-custo';
import { AbaDiaristas } from './aba-diaristas';
import { type AbaDaCalculadora, AbasDaCalculadora } from './kit/abas-da-calculadora';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';

/** As duas abas da calculadora. */
type ChaveDaAba = 'ajuda_custo' | 'diarista';

/** As abas, na ordem da origem. */
const ABAS: readonly AbaDaCalculadora<ChaveDaAba>[] = [
  { chave: 'ajuda_custo', rotulo: 'Ajuda de Custo' },
  { chave: 'diarista', rotulo: 'Diaristas' },
];

interface CalculadoraPetrellaProps {
  residenciaId: string;
  onFechar: () => void;
}

/**
 * O texto de uma falha de gravação ou de leitura.
 *
 * @param erro - O que a mutação ou a consulta lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * A competência de um mês e um ano escritos.
 *
 * @param mes - O mês, de 1 a 12.
 * @param ano - O ano.
 * @returns `AAAA-MM`.
 */
function competenciaDe(mes: string, ano: string): string {
  return `${ano}-${mes.padStart(2, '0')}`;
}

/**
 * A Calculadora Petrella.
 *
 * @param residenciaId - A casa do caixa.
 * @param onFechar - Fecha a caixa: o "✕", o véu, `Esc`, "Cancelar" e a
 *   gravação que deu certo.
 * @returns A caixa de 640 px.
 */
export function CalculadoraPetrella({ residenciaId, onFechar }: CalculadoraPetrellaProps) {
  const notificar = useNotificacao();
  const favorecidos = useFavorecidos(residenciaId).data ?? [];
  const nomes = useNomesDaFolha(residenciaId, true).data ?? [];
  const pagadorPadrao = useConfiguracao(residenciaId).data?.pagadorPadrao ?? '';
  const criar = useCriarPelaCalculadora();
  const buscarUltimoGrupo = useBuscarUltimoGrupo();
  const [aba, setAba] = useState<ChaveDaAba>('ajuda_custo');
  const [ajuda, setAjuda] = useState<CamposDaAjudaDeCusto>(() =>
    camposIniciaisDaAjudaDeCusto(hojeEmIso(), pagadorPadrao),
  );
  const [diarista, setDiarista] = useState<CamposDaDiarista>(() =>
    camposIniciaisDaDiarista(hojeEmIso(), pagadorPadrao),
  );

  /** "Repetir último lançamento" (l. 4922–4974). */
  async function handleRepetir() {
    if (ajuda.funcionario.trim() === '') {
      notificar('Selecione o funcionário primeiro.', false);
      return;
    }
    const favorecido = favorecidoPeloNome(favorecidos, ajuda.funcionario);
    let grupo: Awaited<ReturnType<typeof buscarUltimoGrupo>> = null;
    try {
      grupo = favorecido === undefined ? null : await buscarUltimoGrupo(favorecido.id);
    } catch (erro) {
      notificar(mensagemDe(erro), false);
      return;
    }
    if (grupo === null) {
      notificar('Nenhum lançamento anterior encontrado para esse funcionário.', false);
      return;
    }
    setAjuda(repetirGrupo(ajuda, grupo, favorecido, hojeEmIso()));
    const [ano, mes] = grupo.competencia.split('-');
    notificar(
      `↻ Repetido o lançamento de ${MESES_POR_EXTENSO[Number(mes) - 1] ?? ''}/${ano ?? ''} — confira os valores antes de confirmar.`,
    );
  }

  /** "Criar Lançamentos (Pendente)" (l. 5109–5154). */
  async function handleCriarAjudaDeCusto() {
    const favorecidoNome = ajuda.funcionario.trim();
    if (favorecidoNome === '') {
      notificar('Informe o funcionário.', false);
      return;
    }
    if (ajuda.data === '') {
      notificar('Informe a data do lançamento.', false);
      return;
    }
    const entrada = entradaDaCalculadora(ajuda);
    if (itensDaCalculadora(entrada).length === 0) {
      notificar('Marque ao menos um tipo de ajuda com valor maior que zero.', false);
      return;
    }
    try {
      const { criados } = await criar.mutateAsync({
        aba: 'ajuda_custo',
        residenciaId,
        favorecidoNome,
        cpf: ajuda.cpf,
        pagador: ajuda.pagador.trim() === '' ? pagadorPadrao : ajuda.pagador.trim(),
        competencia: competenciaDe(ajuda.mes, ajuda.ano),
        data: ajuda.data,
        ferias: entrada.ferias,
        ...(entrada.custo === null ? {} : { custo: entrada.custo }),
        ...(entrada.diarias === null ? {} : { diarias: entrada.diarias }),
        ...(entrada.medica === null ? {} : { medica: entrada.medica }),
        ...(entrada.inss === null ? {} : { inss: entrada.inss }),
      });
      onFechar();
      const s = criados > 1 ? 's' : '';
      notificar(
        `✓ ${String(criados)} lançamento${s} de ajuda de custo criado${s} como Pendente`,
      );
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  /** "Criar Lançamento (Pendente)" da aba "Diaristas" (l. 4880–4916). */
  async function handleCriarDiarista() {
    const favorecidoNome = diarista.diarista.trim();
    if (favorecidoNome === '') {
      notificar('Informe a diarista.', false);
      return;
    }
    if (diarista.data === '') {
      notificar('Informe a data do lançamento.', false);
      return;
    }
    if (!(valorDosCamposDaDiarista(diarista) > 0)) {
      notificar('Informe o valor da diária e os dias trabalhados.', false);
      return;
    }
    try {
      await criar.mutateAsync({
        aba: 'diarista',
        residenciaId,
        favorecidoNome,
        competencia: competenciaDe(diarista.mes, diarista.ano),
        data: diarista.data,
        valorDiaria: lerValorDigitado(diarista.diaria),
        dias: Number.parseFloat(diarista.dias) || 0,
      });
      onFechar();
      notificar('✓ Lançamento de diarista criado como Pendente');
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Calculadora Petrella"
      largura={640}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          {aba === 'ajuda_custo' ? (
            <Botao onClick={() => void handleCriarAjudaDeCusto()} disabled={criar.isPending}>
              Criar Lançamentos (Pendente)
            </Botao>
          ) : (
            <Botao onClick={() => void handleCriarDiarista()} disabled={criar.isPending}>
              Criar Lançamento (Pendente)
            </Botao>
          )}
        </>
      }
    >
      <AbasDaCalculadora abas={ABAS} ativa={aba} onTrocar={setAba} />
      {aba === 'ajuda_custo' ? (
        <AbaAjudaDeCusto
          campos={ajuda}
          onMudar={setAjuda}
          favorecidos={favorecidos}
          nomes={nomes}
          onRepetir={() => void handleRepetir()}
        />
      ) : (
        <AbaDiaristas campos={diarista} onMudar={setDiarista} favorecidos={favorecidos} />
      )}
    </CaixaDeDialogo>
  );
}
